'use client';
import { validateCompletedMatchScores } from '@/app/[locale]/match/libs/scoringFormat';
import { pointsForGame } from '@/app/[locale]/match/libs/gameTarget';

import { useEffect, useMemo } from 'react';
import { validateGameScore } from '@/lib/scoring';
import type { DeuceRule, GameFormat } from '@/types/game-format';
import { validateRetiredGames, type RetiredVerdict } from '@/app/[locale]/match/libs/retirement';

export type SingleGameScore = { me: number | ''; opp: number | '' };

export type GameValidation = {
  valid: boolean;
  bothEntered: boolean;
  winner: 'me' | 'opp' | null;
};

export type MatchValidityInfo = {
  isComplete: boolean;
  hasInvalid: boolean;
  hasPartial: boolean;
  requiredGamesToWin: number;
  myGames: number;
  oppGames: number;
  perGame: GameValidation[];
  /** The retirement verdict (me = player 1); null unless a retiree is chosen. */
  retired: RetiredVerdict | null;
};

type Props = {
  bestOf: 1 | 3 | 5;
  value: SingleGameScore[]; // length >= bestOf (extra entries ignored)
  onChange: (next: SingleGameScore[]) => void;
  parTarget?: number;
  decidingGamePoints?: number | null;
  deuceRule?: DeuceRule;
  /** Who retired. A partial last game is accepted only when set (with `retirementFormat`). */
  retiredSide?: 'me' | 'opp' | null;
  retirementFormat?: GameFormat | null;
  onValidityChange?: (isComplete: boolean, info: MatchValidityInfo) => void;
  meLabel?: string;
  oppLabel?: string;
  pointsLabel?: string;
  gamesLabel?: string;
  completeLabel?: string;
  incompleteLabel?: string;
};

function validateParGame(
  a: number,
  b: number,
  target: number,
  deuceRule: DeuceRule = 'win_by_2',
): GameValidation {
  const bothEntered = Number.isFinite(a) && Number.isFinite(b);
  if (!bothEntered) return { valid: false, bothEntered, winner: null };

  const result = validateGameScore(a, b, target, deuceRule);
  if (!result.valid) return { valid: false, bothEntered, winner: null };

  // Map player1/player2 to me/opp
  const winner = result.winner === 'player1' ? ('me' as const) : ('opp' as const);
  return { valid: true, bothEntered, winner };
}

export default function GameScoresInput({
  bestOf,
  value,
  onChange,
  parTarget = 11,
  decidingGamePoints,
  deuceRule = 'win_by_2',
  onValidityChange,
  retiredSide = null,
  retirementFormat = null,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  meLabel,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  oppLabel,
  pointsLabel,
  gamesLabel,
  completeLabel,
  incompleteLabel,
}: Props) {
  const cols = useMemo(() => Array.from({ length: bestOf }, (_, i) => i + 1), [bestOf]);

  const slice = useMemo(() => value.slice(0, bestOf), [value, bestOf]);

  const perGame = useMemo<GameValidation[]>(() => {
    return slice.map((r, index) => {
      const a = typeof r.me === 'number' ? r.me : NaN;
      const b = typeof r.opp === 'number' ? r.opp : NaN;
      return validateParGame(
        a,
        b,
        pointsForGame({ bestOf, pointsPerGame: parTarget, decidingGamePoints }, index + 1),
        deuceRule,
      );
    });
  }, [slice, parTarget, deuceRule, bestOf, decidingGamePoints]);

  const aggregates = useMemo(() => {
    let myPoints = 0;
    let oppPoints = 0;
    let myGames = 0;
    let oppGames = 0;
    slice.forEach((r, idx) => {
      const a = typeof r.me === 'number' ? r.me : 0;
      const b = typeof r.opp === 'number' ? r.opp : 0;
      myPoints += a;
      oppPoints += b;
      const g = perGame[idx];
      if (g?.valid) {
        if (g.winner === 'me') myGames += 1;
        else if (g.winner === 'opp') oppGames += 1;
      }
    });
    return { myPoints, oppPoints, myGames, oppGames };
  }, [slice, perGame]);

  const validity = useMemo<MatchValidityInfo>(() => {
    const requiredGamesToWin = Math.floor(bestOf / 2) + 1;
    const hasInvalid = perGame.some((g) => g.bothEntered && !g.valid);
    const hasPartial = slice.some((r) => {
      const aEntered = typeof r.me === 'number' && Number.isFinite(r.me);
      const bEntered = typeof r.opp === 'number' && Number.isFinite(r.opp);
      return (aEntered && !bEntered) || (!aEntered && bEntered);
    });
    const lastEntered = slice.findLastIndex((r) => r.me !== '' || r.opp !== '');
    const validSequence =
      decidingGamePoints == null ||
      validateCompletedMatchScores(
        slice.slice(0, lastEntered + 1).map((r) => ({ player1Score: r.me, player2Score: r.opp })),
        { bestOf, pointsPerGame: parTarget, decidingGamePoints, deuceRule },
        aggregates.myGames,
        aggregates.oppGames,
      );
    const isComplete =
      validSequence &&
      !hasInvalid &&
      !hasPartial &&
      (aggregates.myGames >= requiredGamesToWin || aggregates.oppGames >= requiredGamesToWin);
    // The entered rows are the games (me = player 1); the verdict decides the partial game.
    const retired =
      retiredSide && retirementFormat
        ? validateRetiredGames(
            slice
              .filter((r) => typeof r.me === 'number' && typeof r.opp === 'number')
              .map((r) => ({ player1: Number(r.me), player2: Number(r.opp) })),
            retirementFormat,
            retiredSide === 'me' ? 'player1' : 'player2',
          )
        : null;
    return {
      isComplete,
      hasInvalid,
      retired,
      hasPartial,
      requiredGamesToWin,
      myGames: aggregates.myGames,
      oppGames: aggregates.oppGames,
      perGame,
    };
  }, [
    bestOf,
    perGame,
    slice,
    aggregates,
    decidingGamePoints,
    parTarget,
    deuceRule,
    retiredSide,
    retirementFormat,
  ]);

  useEffect(() => {
    onValidityChange?.(validity.isComplete, validity);
  }, [validity, onValidityChange]);

  // The unfinished game of an accepted retirement is dashed, not red.
  const partialIndex =
    validity.retired?.ok && validity.retired.partial ? validity.retired.completed.length : -1;
  const cellTone = (i: number) =>
    i === partialIndex
      ? 'border-dashed'
      : perGame[i]?.bothEntered && !perGame[i]?.valid
        ? 'border-red-500'
        : '';

  const setCell = (index: number, which: 'me' | 'opp', val: number | '') => {
    onChange(value.map((r, i) => (i === index ? { ...r, [which]: val } : r)));
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr>
            {cols.map((c) => (
              <th
                key={`h-${c}`}
                className="bg-primary/15 border-primary/30 text-foreground/90 rounded-sm border px-2 py-1 text-center font-medium"
              >
                {c}
              </th>
            ))}
            <th className="bg-primary/15 border-primary/30 text-foreground/90 rounded-sm border px-2 py-1 text-center font-medium">
              {pointsLabel || 'Points'}
            </th>
            <th className="bg-primary/15 border-primary/30 text-foreground/90 rounded-sm border px-2 py-1 text-center font-medium">
              {gamesLabel || 'Games'}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            {slice.map((r, i) => (
              <td key={`me-${i}`} className="p-1">
                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  className={`bg-background w-full rounded-md border px-2 py-1 text-sm ${cellTone(
                    i,
                  )}`}
                  placeholder="0"
                  value={r.me as number | ''}
                  onChange={(e) =>
                    setCell(i, 'me', e.target.value === '' ? '' : Number(e.target.value))
                  }
                  tabIndex={i * 2 + 1}
                />
              </td>
            ))}
            <td className="bg-foreground/10 rounded-sm px-2 py-1 text-center">
              {aggregates.myPoints}
            </td>
            <td className="bg-foreground/10 rounded-sm px-2 py-1 text-center">
              {aggregates.myGames}
            </td>
          </tr>
          <tr>
            {slice.map((r, i) => (
              <td key={`opp-${i}`} className="p-1">
                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  className={`bg-background w-full rounded-md border px-2 py-1 text-sm ${cellTone(
                    i,
                  )}`}
                  placeholder="0"
                  value={r.opp as number | ''}
                  onChange={(e) =>
                    setCell(i, 'opp', e.target.value === '' ? '' : Number(e.target.value))
                  }
                  tabIndex={i * 2 + 2}
                />
              </td>
            ))}
            <td className="bg-foreground/10 rounded-sm px-2 py-1 text-center">
              {aggregates.oppPoints}
            </td>
            <td className="bg-foreground/10 rounded-sm px-2 py-1 text-center">
              {aggregates.oppGames}
            </td>
          </tr>
        </tbody>
      </table>
      <div className="mt-2 flex items-center justify-between text-xs">
        <div className="text-foreground/60">
          PAR {parTarget}
          {deuceRule === 'sudden_death' ? ' SD' : ''} • {validity.requiredGamesToWin} to win
        </div>
        <div
          className={`${
            validity.isComplete || validity.retired?.ok ? 'text-green-700' : 'text-red-700'
          } rounded px-2 py-0.5`}
        >
          {validity.isComplete || validity.retired?.ok
            ? completeLabel || 'Complete'
            : incompleteLabel || 'Incomplete'}
        </div>
      </div>
    </div>
  );
}
